import {useCallback, useEffect, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useTranslations} from 'use-intl'
import Filter, {ProductFilters} from '~/components/product-list/Filter'
import ResponsiveDialog from '~/components/common/ResponsiveDialog'
import {productFilterQueryOptions} from '~/client/fn/productFilter'
import {Button} from '~/components/common/Button'
import {useIsMinMedium} from '~/hooks/useScreen'

type MobileFilterDialogProps = {
    open: boolean
    onClose: (filter: ProductFilters) => void
    activeFilter?: ProductFilters
}

const MobileFilterDialog = ({
    activeFilter: initialActiveFilter,
    open,
    onClose,
}: MobileFilterDialogProps) => {
    const t = useTranslations('productList.filter')
    const isDesktop = useIsMinMedium()

    const [activeFilter, setActiveFilter] = useState<ProductFilters>(initialActiveFilter ?? {})

    useEffect(() => {
        setActiveFilter(initialActiveFilter ?? {})
    }, [initialActiveFilter])

    const applyFiltersAndClose = useCallback(() => {
        onClose({
            departmentId: activeFilter.departmentId,
            categoryId: activeFilter.categoryId,
            appearanceId: activeFilter.appearanceId,
            sizeId: activeFilter.sizeId,
        })
    }, [
        onClose,
        activeFilter.departmentId,
        activeFilter.categoryId,
        activeFilter.appearanceId,
        activeFilter.sizeId,
    ])

    useEffect(() => {
        if (open && isDesktop) {
            applyFiltersAndClose()
        }
    }, [isDesktop])

    const {data: filter} = useQuery(
        productFilterQueryOptions(activeFilter.departmentId, activeFilter.categoryId)
    )

    return (
        <ResponsiveDialog
            open={open}
            onOpenChange={applyFiltersAndClose}
            title={t('title')}
            description={t('mobileDescription')}
        >
            {!!filter && (
                <div className="pb-8 flex flex-col overflow-hidden">
                    <div className="overflow-y-auto">
                        <Filter
                            filter={filter}
                            activeFilter={activeFilter}
                            onSelect={setActiveFilter}
                        />
                    </div>
                    <div className="mx-4 mt-6">
                        <Button className="w-full" variant="primary" onClick={applyFiltersAndClose}>
                            {t('applyFilters')}
                        </Button>
                    </div>
                </div>
            )}
        </ResponsiveDialog>
    )
}

export default MobileFilterDialog
